import { observer } from 'mobx-react-lite'
import { DataTableRowSkeleton } from '@/components/DataTable'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import {
  UserList,
  type UserListColumnId,
  userListColumns,
} from '@/features/iam/users/components/UserList'
import { type ClientUserManagementViewModel } from '@/features/client-user-management/ClientUserManagement/ClientUserManagementViewModel'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'
import { useFeatures } from '@/contexts/root-store-context'

export interface ClientListContainerProps {
  vm: ClientUserManagementViewModel
}

export const ClientListContainer = observer(function ClientListContainer({
  vm,
}: ClientListContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  const layout = vm.columnLayout

  return (
    <UserList
      variant="clients"
      isShowingInactiveUsers={vm.userSearch.userInactiveFilter.value}
      onInactiveChange={vm.userSearch.userInactiveFilter.change}
      totalUsers={vm.userSearch.totalUsers}
      showStatusTabs
      searchText={vm.userSearch.searchText.value}
      onSearchTextChange={vm.userSearch.searchText.change}
      columnWidths={
        Object.fromEntries(
          userListColumns.map((column) => [column.id, layout.widthOf(column)]),
        ) as Record<UserListColumnId, number>
      }
      onResize={layout.setWidth}
      onResizeEnd={layout.save}
      showEmptyState={vm.userSearch.shouldShowEmptyState}
      emptyState={
        <NoResultsEmptyState
          isSearchFilterApplied={vm.userSearch.isSearchFilterApplied}
          onClearSearchFilter={vm.userSearch.clearFilters}
          corporateMode={shellInfo.corporateMode}
        />
      }
      rows={vm.userSearch.users.map((user) => ({
        publicId: user.publicId,
        displayName: user.displayName,
        avatar: user.avatar,
        online: user.online,
        email: user.email,
        lastSeen: user.lastSeen,
        dateCreated: user.dateCreated,
        href: vm.getUserDetailsLinkForUser(user),
      }))}
      isSkeleton={vm.userSearch.isFetchingInitial}
      scrollLoader={
        <SkeletonListInfiniteScrollTrigger
          scroller={vm.userSearch.scrollProvider}
          maxSkeletons={10}
        >
          <DataTableRowSkeleton />
        </SkeletonListInfiniteScrollTrigger>
      }
    />
  )
})
